import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Calendar, Clock, CheckCircle2, AlertCircle, Repeat, Pause,
  Play, Trash2, ChevronRight, CalendarDays, Wrench, Search,
  ShieldCheck, BookOpen, KeyRound, GraduationCap, HardDrive,
  Users, ScanLine
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const categoryConfig = {
  policy_review: { labelKey: "maint.policy_review", icon: BookOpen, color: "text-blue-500" },
  control_update: { labelKey: "maint.control_update", icon: ShieldCheck, color: "text-purple-500" },
  certificate_renewal: { labelKey: "maint.cert_renewal", icon: KeyRound, color: "text-orange-500" },
  training: { labelKey: "maint.training", icon: GraduationCap, color: "text-green-500" },
  backup_verification: { labelKey: "maint.backup_verify", icon: HardDrive, color: "text-cyan-500" },
  access_review: { labelKey: "maint.access_review", icon: Users, color: "text-pink-500" },
  vulnerability_scan: { labelKey: "maint.vuln_scan", icon: ScanLine, color: "text-red-500" },
  other: { labelKey: "maint.other", icon: Wrench, color: "text-gray-500" }
};

const frequencyLabelKeys = {
  daily: "maint.daily", weekly: "maint.weekly", monthly: "maint.monthly",
  quarterly: "maint.quarterly", semi_annual: "maint.semi_annual", annual: "maint.annual"
};

const statusConfig = {
  active: { labelKey: "active", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  paused: { labelKey: "paused", color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400" },
  overdue: { labelKey: "overdue", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  completed: { labelKey: "completed", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" }
};

const Maintenance = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [newTask, setNewTask] = useState({
    title: "", description: "", category: "policy_review",
    frequency: "monthly", next_due_date: "", owner: ""
  });

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/maintenance-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const createTask = async () => {
    if (!newTask.title || !newTask.next_due_date) {
      toast.error(t("maint.title_date_required"));
      return;
    }
    try {
      await axios.post(`${API}/maintenance-tasks`, newTask, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("maint.task_created"));
      setShowCreate(false);
      setNewTask({ title: "", description: "", category: "policy_review", frequency: "monthly", next_due_date: "", owner: "" });
      fetchTasks();
    } catch (error) {
      toast.error(t("maint.create_failed"));
    }
  };

  const completeTask = async (taskId) => {
    try {
      const res = await axios.put(`${API}/maintenance-tasks/${taskId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${t("maint.complete")}! ${t("maint.next_due")}: ${res.data.next_due_date}`);
      fetchTasks();
    } catch (error) {
      toast.error(t("maint.complete_failed"));
    }
  };

  const togglePause = async (taskId, currentStatus) => {
    const newStatus = currentStatus === "paused" ? "active" : "paused";
    try {
      await axios.put(`${API}/maintenance-tasks/${taskId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t(newStatus === "paused" ? "paused" : "active"));
      fetchTasks();
    } catch (error) {
      toast.error(t("maint.status_update_failed"));
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/maintenance-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("maint.task_deleted"));
      fetchTasks();
    } catch (error) {
      toast.error(t("maint.delete_failed"));
    }
  };

  const filtered = tasks.filter(t => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const today = new Date().toISOString().split("T")[0];
  const overdueCount = tasks.filter(t => t.next_due_date < today && t.status === "active").length;
  const dueThisWeek = tasks.filter(t => {
    const due = new Date(t.next_due_date);
    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    return due <= weekFromNow && due >= new Date(today) && t.status === "active";
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t("maint.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="maintenance-page">
      <PageHeader
        title={t("maint.title")}
        description={t("maint.subtitle")}
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("maint.new")}
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold">{tasks.length}</p>
            <p className="text-xs text-muted-foreground">{t("maint.total_tasks")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold text-green-500">{tasks.filter(t => t.status === "active").length}</p>
            <p className="text-xs text-muted-foreground">{t("active")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold text-red-500">{overdueCount + tasks.filter(t => t.status === "overdue").length}</p>
            <p className="text-xs text-muted-foreground">{t("overdue")}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-heading font-bold text-yellow-500">{dueThisWeek}</p>
            <p className="text-xs text-muted-foreground">{t("maint.due_this_week")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("maint.search_tasks")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10" data-testid="task-search-input" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36" data-testid="task-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("maint.all_status")}</SelectItem>
            <SelectItem value="active">{t("active")}</SelectItem>
            <SelectItem value="paused">{t("paused")}</SelectItem>
            <SelectItem value="overdue">{t("overdue")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-44" data-testid="task-filter-category">
            <SelectValue placeholder={t("category")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("maint.all_categories")}</SelectItem>
            {Object.entries(categoryConfig).map(([k, v]) => (
              <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Task List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filtered.map((task, index) => {
            const cat = categoryConfig[task.category] || categoryConfig.other;
            const CatIcon = cat.icon;
            const isOverdue = task.next_due_date < today && task.status === "active";

            return (
              <motion.div key={task.task_id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2, delay: index * 0.02 }}
              >
                <Card className={`border-border hover:border-primary/50 transition-colors ${isOverdue ? 'border-red-500/50' : ''}`}
                  data-testid={`task-card-${task.task_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0`}>
                          <CatIcon className={`w-5 h-5 ${cat.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className={statusConfig[isOverdue ? 'overdue' : task.status]?.color}>
                              {isOverdue ? t("overdue") : t(statusConfig[task.status]?.labelKey)}
                            </Badge>
                            <Badge variant="secondary">{t(frequencyLabelKeys[task.frequency])}</Badge>
                            <Badge variant="secondary" className="capitalize">{t(cat.labelKey)}</Badge>
                          </div>
                          <p className="font-medium text-sm truncate">{task.title}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-3 h-3" />
                              {t("maint.due_date")}: {new Date(task.next_due_date).toLocaleDateString()}
                            </span>
                            {task.owner && <span>{t("owner")}: {task.owner}</span>}
                            {task.last_completed_date && (
                              <span>{t("maint.last_completed")}: {new Date(task.last_completed_date).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); completeTask(task.task_id); }}
                          data-testid={`complete-task-${task.task_id}`}
                          title={t("maint.complete")}>
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); togglePause(task.task_id, task.status); }}
                          title={task.status === "paused" ? t("maint.resume") : t("maint.pause")}>
                          {task.status === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); deleteTask(task.task_id); }}
                          title={t("delete")}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {tasks.length === 0 ? t("maint.no_tasks") : t("maint.no_tasks_filter")}
            </p>
            {tasks.length === 0 && (
              <Button className="mt-4" onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" /> {t("maint.schedule_first")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("maint.new")}</DialogTitle>
            <DialogDescription>{t("maint.new_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>{t("maint.task_title")} *</Label>
              <Input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="e.g., Annual ISMS Policy Review" data-testid="task-title-input" />
            </div>
            <div className="space-y-2">
              <Label>{t("description")}</Label>
              <Textarea value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder={t("maint.task_details_ph")} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("category")}</Label>
                <Select value={newTask.category} onValueChange={(v) => setNewTask({ ...newTask, category: v })}>
                  <SelectTrigger data-testid="task-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryConfig).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("maint.frequency")}</Label>
                <Select value={newTask.frequency} onValueChange={(v) => setNewTask({ ...newTask, frequency: v })}>
                  <SelectTrigger data-testid="task-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(frequencyLabelKeys).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{t(v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("maint.next_due_date")} *</Label>
                <Input type="date" value={newTask.next_due_date}
                  onChange={(e) => setNewTask({ ...newTask, next_due_date: e.target.value })}
                  data-testid="task-due-date-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("owner")}</Label>
                <Input value={newTask.owner} onChange={(e) => setNewTask({ ...newTask, owner: e.target.value })}
                  placeholder={t("maint.assigned_to_ph")} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel")}</Button>
            <Button onClick={createTask} data-testid="save-task-btn">{t("maint.create_task")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Maintenance;
